import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { SkillBridgeApply } from '@/components/jobs/SkillBridgeApply';
import { Send, ExternalLink, Check, Loader2, RefreshCw, Sparkles, AlertCircle, Eye, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Lightweight external tracking: seeker applies on the original site, then can tell Sergeant
// TAP they applied. Creates/updates the ONE canonical external application (application_source
// = external) with a TRUTHFUL seeker-reported event. No fabricated employer views/responses.
function ExternalApplySection({ job, user, url }) {
  const isJobSeeker = user?.role === 'job_seeker';
  const [tracked, setTracked] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [stage, setStage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!isJobSeeker) return;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/applications/my-applications`, { withCredentials: true });
        const mine = (res.data || []).find((a) => a.job_id === job.job_id);
        if (!cancelled && mine) { setTracked(true); setStage(mine.current_lifecycle_state || 'submitted'); }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [job.job_id, isJobSeeker]);

  const markApplied = async () => {
    setTracking(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/applications/external/track`,
        { job_id: job.job_id }, { withCredentials: true });
      setTracked(true);
      setStage(res.data?.current_lifecycle_state || 'submitted');
      toast.success('Marked as applied. We saved this to your In Motion list.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save that right now');
    } finally {
      setTracking(false);
    }
  };

  return (
    <div className="text-center space-y-3" data-testid="external-apply-section">
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center w-full h-12 bg-brand hover:bg-brand-hover text-white rounded-lg text-sm font-medium transition-colors"
          data-testid="apply-external-btn"
        >
          <ExternalLink className="w-5 h-5 mr-2" />
          Apply on Original Site
        </a>
      ) : (
        <Button disabled className="w-full h-12 opacity-50" data-testid="apply-external-unavailable-btn">
          Original Posting Unavailable
        </Button>
      )}

      {isJobSeeker && (
        tracked ? (
          <div className="flex items-center justify-center gap-2 text-sm text-brand font-medium" data-testid="external-tracked-state">
            <Check className="w-4 h-4" />
            You told us you applied
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={markApplied}
            disabled={tracking}
            className="w-full h-11 border-slate-300 text-slate-700"
            data-testid="external-i-applied-btn"
          >
            {tracking ? 'Saving…' : "I Applied"}
          </Button>
        )
      )}
      <p className="text-xs text-slate-400">
        Applications for this job are handled on the original site. Marking “I Applied” only
        saves your own record — Sergeant TAP can’t see the employer’s response.
      </p>
    </div>
  );
}

// Canonical applicant-facing actions for an opportunity — the SAME behavior used by the
// full JobDetails page and the Opportunities map preview. Application endpoints, lifecycle,
// duplicate-prevention and state semantics are unchanged; this is a verbatim extraction so
// SkillBridge, external and native employment actions share one source of truth.
// NOTE: owner/admin management controls intentionally live in JobDetails, not here.
export function OpportunityActions({ job, user, resume, onApplyOpenChange }) {
  const navigate = useNavigate();
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  // Application-specific cover letter DRAFT (AI-generated, seeker-editable). Never seeded
  // from the legacy generic resume cover letter. Blank = the seeker submits no cover letter.
  const [coverLetter, setCoverLetter] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [applying, setApplying] = useState(false);
  const genRequested = useRef(false); // guards against duplicate generation on re-render
  // Existing canonical application for (this job, this seeker) — checked BEFORE any AI call.
  const [existingApp, setExistingApp] = useState(null);
  const [checkedExisting, setCheckedExisting] = useState(false);

  const isAuthed = !!user;
  const isJobSeeker = user?.role === 'job_seeker';
  const isJobActive = job?.status === 'active';
  const isNativeJob = job?.opportunity_type !== 'skillbridge' && job?.source_type !== 'external';

  // Session-scoped draft cache keys (smallest safe reuse — no second document model).
  const draftKey = `stap_cl_draft_${job.job_id}`;
  const genKey = `stap_cl_gen_${job.job_id}`;

  // Duplicate-application check runs on mount for native jobs — the Apply action must
  // recognize an existing application BEFORE opening the form or calling AI generation.
  useEffect(() => {
    if (!(isAuthed && isJobSeeker && isNativeJob)) { setCheckedExisting(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/applications/my-applications`, { withCredentials: true });
        const mine = (res.data || []).find((a) => a.job_id === job.job_id);
        if (!cancelled) setExistingApp(mine || null);
      } catch { /* non-fatal */ }
      finally { if (!cancelled) setCheckedExisting(true); }
    })();
    return () => { cancelled = true; };
  }, [job.job_id, isAuthed, isJobSeeker, isNativeJob]);

  const generateCoverLetter = useCallback(async () => {
    setGenerating(true);
    setGenError(null);
    // Mark that a generation was attempted this session so reopening won't auto-call AI again.
    try { sessionStorage.setItem(genKey, '1'); } catch { /* ignore */ }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/applications/generate-cover-letter`,
        { job_id: job.job_id },
        { withCredentials: true }
      );
      if (res.data?.generated && res.data?.cover_letter) {
        setCoverLetter(res.data.cover_letter);
        try { sessionStorage.setItem(draftKey, res.data.cover_letter); } catch { /* ignore */ }
      } else {
        // Throttled (cooldown/cap) or failed — KEEP the current draft intact.
        setGenError(res.data?.error || "We couldn't generate a draft. You can write one manually or apply without one.");
      }
    } catch (error) {
      setGenError(error.response?.data?.detail || "We couldn't generate a draft. You can write one manually or apply without one.");
    } finally {
      setGenerating(false);
    }
  }, [job.job_id, draftKey, genKey]);

  const openApply = () => {
    if (existingApp) { navigate(`/applications/${existingApp.application_id}`); return; }
    if (!resume?.is_complete) {
      toast.error('Please complete your resume first');
      navigate('/resume-builder');
      return;
    }
    setApplyOpen(true);
  };

  const setApplyOpen = (open) => {
    setApplyDialogOpen(open);
    onApplyOpenChange?.(open);
    if (open) {
      // Reuse the most recent unsubmitted draft for this seeker+job during this session.
      try {
        const cached = sessionStorage.getItem(draftKey);
        if (cached != null) setCoverLetter(cached);
      } catch { /* ignore */ }
      setGenError(null);
    }
    // On close we intentionally keep the cached draft so reopening returns it unchanged.
  };

  // Auto-generate ONE draft on first eligible open — never when an application already exists,
  // and never if a draft was already generated/attempted or cached this session.
  useEffect(() => {
    if (!applyDialogOpen || existingApp) return;
    let attempted = false, hasCached = false;
    try { attempted = sessionStorage.getItem(genKey) === '1'; hasCached = sessionStorage.getItem(draftKey) != null; }
    catch { /* ignore */ }
    if (!attempted && !hasCached && !genRequested.current) {
      genRequested.current = true;
      generateCoverLetter();
    }
  }, [applyDialogOpen, existingApp, generateCoverLetter, genKey, draftKey]);

  const handleApply = async () => {
    if (!resume?.is_complete) {
      toast.error('Please complete your resume first');
      navigate('/resume-builder');
      return;
    }
    setApplying(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/jobs/${job.job_id}/apply`,
        { cover_letter: coverLetter.trim() },
        { withCredentials: true }
      );
      toast.success('Application submitted successfully!');
      // Freeze complete: clear the session draft cache for this job.
      try { sessionStorage.removeItem(draftKey); sessionStorage.removeItem(genKey); } catch { /* ignore */ }
      setApplyOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Application failed');
    } finally {
      setApplying(false);
    }
  };

  // SKILLBRIDGE: tracked Sergeant TAP application (never the native in-app apply flow)
  if (job.opportunity_type === 'skillbridge') {
    return <SkillBridgeApply job={job} user={user} onApplyOpenChange={onApplyOpenChange} />;
  }

  // EXTERNAL JOB: apply on the original site, then optionally tell Sergeant TAP you applied
  if (job.source_type === 'external') {
    const url = job.apply_url || job.canonical_url || job.source_url;
    return <ExternalApplySection job={job} user={user} url={url} />;
  }

  // NATIVE EMPLOYMENT
  const APPLIED_STAGE = {
    started: 'Applied', submitted: 'Applied', contacted: 'Under review',
    interview: 'Interviewing', offer: 'Offer extended', hired: 'Hired',
    not_selected: 'Not selected', withdrawn: 'Withdrawn',
  };
  const appliedStage = existingApp
    ? (APPLIED_STAGE[existingApp.current_lifecycle_state] || 'Applied')
    : null;
  const appliedDate = existingApp?.applied_at
    ? new Date(existingApp.applied_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <>
      {/* Already applied — recognized BEFORE any Apply form / AI generation */}
      {isAuthed && isJobSeeker && existingApp && (
        <div className="rounded-lg border border-brand/20 bg-brand-light/40 p-4" data-testid="already-applied-state">
          <div className="flex items-center gap-2 text-brand font-semibold text-sm">
            <Check className="w-4 h-4" /> You already applied to this role
          </div>
          <p className="text-xs text-slate-500 mt-1" data-testid="already-applied-meta">
            {appliedDate ? `Applied ${appliedDate}` : 'Applied'} · {appliedStage}
          </p>
          <div className="flex gap-2 mt-3">
            <Button
              data-testid="view-application-btn"
              onClick={() => navigate(`/applications/${existingApp.application_id}`)}
              className="flex-1 h-10 bg-brand hover:bg-brand-hover text-white"
            >
              <Eye className="w-4 h-4 mr-2" /> View Application
            </Button>
            <Button
              data-testid="message-employer-btn"
              variant="outline"
              onClick={() => navigate(`/messages?application=${existingApp.application_id}`)}
              className="flex-1 h-10 border-slate-300 text-slate-700"
            >
              <MessageSquare className="w-4 h-4 mr-2" /> Message Employer
            </Button>
          </div>
        </div>
      )}

      {/* Authenticated Job Seeker + Active Job (only when NOT already applied) */}
      {isAuthed && isJobSeeker && isJobActive && !existingApp && (
        <Button
          data-testid="apply-now-btn"
          onClick={openApply}
          disabled={!checkedExisting}
          className="w-full h-12 bg-brand hover:bg-brand-hover text-white"
        >
          <Send className="w-5 h-5 mr-2" />
          {checkedExisting ? 'Apply Now' : 'Checking…'}
        </Button>
      )}

      {/* Job Seeker but job is inactive */}
      {isAuthed && isJobSeeker && !isJobActive && !existingApp && (
        <div className="text-center py-4 bg-slate-50 rounded-lg">
          <p className="text-slate-600">This position is no longer accepting applications.</p>
        </div>
      )}

      {/* Authenticated but NOT a job seeker (owner/admin management lives in JobDetails) */}
      {isAuthed && !isJobSeeker && (
        <div className="text-center py-4 bg-slate-50 rounded-lg" data-testid="non-applicant-message">
          <p className="text-slate-600">Applications are available to candidates only.</p>
        </div>
      )}

      {/* Not authenticated */}
      {!isAuthed && (
        <Button
          data-testid="sign-in-to-apply-btn"
          onClick={() => navigate('/auth')}
          className="w-full h-12 bg-brand hover:bg-brand-hover text-white"
        >
          Sign In to Apply
        </Button>
      )}

      {/* Apply Dialog */}
      <Dialog open={applyDialogOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-2xl" data-testid="apply-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">Apply to {job.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            {/* Resume — the immutable submitted-resume mechanism from Prompt 4 */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-4" data-testid="apply-resume-note">
              <p className="text-sm font-medium text-slate-900">Resume</p>
              <p className="text-sm text-slate-600 mt-0.5">
                Your current resume will be submitted and saved as the version for this
                application. Later edits to your profile won’t change what you sent here.
              </p>
            </div>

            {/* AI-generated, seeker-editable application-specific cover letter */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <Label className="text-slate-900 font-medium">
                  Cover Letter
                </Label>
                <div className="flex items-center gap-2">
                  {coverLetter.trim() && !generating && (
                    <Button
                      type="button" size="sm" variant="ghost"
                      className="h-8 text-xs text-slate-500 hover:text-slate-700"
                      onClick={() => { setCoverLetter(''); try { sessionStorage.setItem(draftKey, ''); } catch { /* ignore */ } }}
                      data-testid="clear-cover-letter-btn"
                    >
                      Clear
                    </Button>
                  )}
                  <Button
                    type="button" size="sm" variant="outline"
                    className="h-8 text-xs border-slate-200"
                    onClick={generateCoverLetter}
                    disabled={generating}
                    data-testid="regenerate-cover-letter-btn"
                  >
                    {generating
                      ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                    {coverLetter.trim() ? 'Regenerate' : 'Generate'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-2" data-testid="cover-letter-edit-affordance">
                Review and edit before submitting.
              </p>

              {generating && !coverLetter && (
                <div className="flex items-center gap-2 text-sm text-brand py-6 justify-center bg-brand-light/50 rounded-md border border-brand/10" data-testid="cover-letter-generating">
                  <Sparkles className="w-4 h-4 animate-pulse" />
                  Preparing your cover letter…
                </div>
              )}

              {genError && !generating && (
                <div className="flex items-start gap-2 p-3 mb-2 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="cover-letter-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{genError}</span>
                </div>
              )}

              {(!generating || coverLetter) && (
                <Textarea
                  data-testid="cover-letter-textarea"
                  value={coverLetter}
                  onChange={(e) => { setCoverLetter(e.target.value); try { sessionStorage.setItem(draftKey, e.target.value); } catch { /* ignore */ } }}
                  className="min-h-[240px]"
                  placeholder="Your tailored cover letter will appear here. You can edit every word before submitting."
                />
              )}

              <p className="text-xs text-slate-500 mt-2" data-testid="apply-cover-letter-hint">
                {coverLetter.trim()
                  ? 'Review and edit this letter — the exact text you see will be sent and saved as your submitted version.'
                  : 'Leave this blank to apply without a cover letter — the employer will see that none was provided.'}
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                data-testid="cancel-apply-btn"
                onClick={() => setApplyOpen(false)}
                variant="outline"
                className="border-slate-300 text-slate-700"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-application-btn"
                onClick={handleApply}
                disabled={applying || generating}
                className="bg-brand hover:bg-brand-hover text-white"
              >
                {applying ? 'Submitting...' : 'Submit Application'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default OpportunityActions;
